import { createCanceledError } from '../../../errors/createCanceledError';
import { PhotoValidationCompletedEventPayload, ValidationWorkerEventPayload } from '../../../types';
import logger from '../../../libs/logger/Logger';

const WORKER_PATH = './js/intakeWorker/index.js';
const WORKER_LIMIT = 2;
const WORKER_TIMEOUT_MS = 90000;
const TERMINATE_INACTIVE_WORKERS_AFTER_MS = 5 * 60 * 1000;

/**
 * Manages a fleet of Validation Workers
 */
class ValidationWorkerManager {
  private workers: {
    worker: Worker;
    busy: boolean;
  }[] = [];
  private inactivityTimer: number | null = null;
  private lastActivityTime: number = Date.now();

  constructor() {
    console.log(`Validation worker limit: ${WORKER_LIMIT}`);
  }

  validate(data: ValidationWorkerEventPayload, abortSignal: AbortSignal) {
    this.resetInactivityTimer();

    const vacantWorkerIndex = this.getVacantWorkerIndex();

    if (vacantWorkerIndex === -1) {
      throw new Error('No vacant workers available');
    }

    this.workers[vacantWorkerIndex].busy = true;

    return new Promise<{ eventType: string; payload: PhotoValidationCompletedEventPayload }>((resolve, reject) => {
      const worker = this.workers[vacantWorkerIndex].worker;
      let timeoutId: number | null = window.setTimeout(() => {
        this.replaceWorker(vacantWorkerIndex);
        reject(new Error('Worker validation timed out'));
      }, WORKER_TIMEOUT_MS);

      // Check if already aborted
      if (abortSignal.aborted) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
        this.workers[vacantWorkerIndex].busy = false;
        reject(createCanceledError());
        return;
      }

      // Set up abort handler
      const abortHandler = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
        this.workers[vacantWorkerIndex].busy = false;
        this.replaceWorker(vacantWorkerIndex);
        reject(createCanceledError());
      };

      abortSignal.addEventListener('abort', abortHandler);

      worker.onmessage = (event) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;

        this.workers[vacantWorkerIndex].busy = false;
        this.resetInactivityTimer();

        abortSignal.removeEventListener('abort', abortHandler);

        resolve(event.data);
      };

      worker.onerror = (error) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;

        // Workers import a bunch of libs that look like they may cause memory leaks, let's recreate failed workers just in case
        this.replaceWorker(vacantWorkerIndex);

        abortSignal.removeEventListener('abort', abortHandler);

        reject(new Error(`Worker error: ${error.message}`));
      };

      worker.postMessage(data);
    });
  }

  private addWorker() {
    const worker = new Worker(WORKER_PATH);
    const index = this.workers.length;
    this.workers.push({
      worker,
      busy: false,
    });
    return index;
  }

  private replaceWorker(index: number) {
    this.workers[index].worker.terminate();
    const worker = new Worker(WORKER_PATH);
    this.workers[index] = {
      worker,
      busy: false,
    };
  }

  private getVacantWorkerIndex() {
    // First check for existing vacant workers
    const existingVacantIndex = this.workers.findIndex((w) => !w.busy);
    if (existingVacantIndex !== -1) {
      return existingVacantIndex;
    }

    // If we have fewer workers than the limit, create a new one
    if (this.workers.length < WORKER_LIMIT) {
      return this.addWorker();
    }

    return -1; // No vacant workers and at the limit
  }

  get vacantWorkers() {
    const currentCount = this.workers.filter((w) => !w.busy).length;
    const potentialNewWorkers = Math.max(0, WORKER_LIMIT - this.workers.length);
    return currentCount + potentialNewWorkers;
  }

  private resetInactivityTimer(): void {
    this.lastActivityTime = Date.now();

    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = window.setTimeout(() => {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - this.lastActivityTime;

      if (timeSinceLastActivity >= TERMINATE_INACTIVE_WORKERS_AFTER_MS) {
        // Terminate all workers if there's been no activity
        this.workers.forEach((w) => w.worker.terminate());
        logger.log(
          `Terminated validation workers after ${
            TERMINATE_INACTIVE_WORKERS_AFTER_MS / 60 / 1000
          } minutes of inactivity`,
        );
        this.workers = [];
      } else {
        // If there was activity, reset the timer
        this.resetInactivityTimer();
      }
    }, TERMINATE_INACTIVE_WORKERS_AFTER_MS);
  }
}

export const hasHitParallelValidationLimit = () =>
  (typeof Worker === 'undefined' ? Infinity : validationWorkerManager.vacantWorkers) <= 0;

export const validationWorkerManager = new ValidationWorkerManager();
